import { toast, ToastOptions } from 'react-toastify';
import { ChatIcon, ToastError, ToastSuccess } from '@vgrubs/components';
import styled from 'styled-components';
import { IconMessageWrap, IconWrap } from './ToastsStyled';

/**
 * Shared by every toast in the app.
 *
 * Two things deliberately absent. `position` belongs to the container: a toast
 * may override it, and one that did would be drawn outside the stack the
 * others are in. And the custom slide-down transition it used to carry is gone
 * with the stacking - it ended on `transform: translateY(0)` with a fill mode
 * that keeps it, which beat the offset the stack positions each toast with and
 * left the whole pile sitting on top of itself.
 */
const toastConfig: ToastOptions = {
  icon: false,
  hideProgressBar: true,
  pauseOnHover: true,
  autoClose: 5000,
  draggable: true,
  closeOnClick: true,
};

export const SuccessToast = (successMessage: string) => {
  toast.success(
    <SuccessToastComponent successMessage={successMessage ?? 'Success'} />,
    toastConfig
  );
};

export const ErrorToast = (errorMessage: string) => {
  if (errorMessage.includes('Unauthorized')) {
    return;
  }
  if (!errorMessage) return;
  toast.error(
    <ErrorToastComponent errorMessage={errorMessage ?? 'Error'} />,
    toastConfig
  );
};

export const SuccessToastComponent = ({
  successMessage,
}: {
  successMessage: string;
}) => (
  <IconMessageWrap>
    <IconWrap>
      <ToastSuccess />
    </IconWrap>
    <div>
      <h5>Success</h5>
      <p>{successMessage}</p>
    </div>
  </IconMessageWrap>
);

export const ErrorToastComponent = ({
  errorMessage,
}: {
  errorMessage: string;
}) => (
  <IconMessageWrap>
    <IconWrap>
      <ToastError />
    </IconWrap>
    <div>
      <h5>Error</h5>
      <p>{errorMessage}</p>
    </div>
  </IconMessageWrap>
);

/**
 * A message arrived while the merchant was somewhere else in the app.
 *
 * In-app on purpose, alongside the system notification rather than instead of
 * it: whether a notification is ever drawn on screen is the operating system's
 * decision - macOS shows Chrome's as a banner that hides itself unless the user
 * has set that app to Alerts - and this one is ours to guarantee.
 *
 * Held longer than the usual toast and opened by a click, since the whole point
 * is to be noticed and acted on rather than acknowledged.
 */
export const ChatMessageToast = (
  preview: string,
  onClick?: () => void,
  /**
   * Who it is from. Defaults to the merchant's only correspondent; the admin
   * app passes the conversation, since there it could be any of them.
   */
  title?: string
) => {
  toast(<ChatMessageToastComponent preview={preview} title={title} />, {
    ...toastConfig,
    autoClose: 8000,
    onClick,
  });
};

/**
 * The launcher's disc, shrunk. `ChatIcon` is drawn white for the red sidebar
 * and takes no colour, so on a white toast it needs the disc behind it - which
 * is also what makes the toast read as the same thing as the bubble it opens.
 */
const ChatToastIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ theme }) => theme.primary};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const ChatMessageToastComponent = ({
  preview,
  title,
}: {
  preview: string;
  title?: string;
}) => (
  <IconMessageWrap>
    <IconWrap>
      <ChatToastIcon>
        <ChatIcon />
      </ChatToastIcon>
    </IconWrap>
    <div>
      <h5>{title || 'vGrubs Support'}</h5>
      <p>{preview}</p>
    </div>
  </IconMessageWrap>
);
